// src/pages/Legal.jsx
// Public legal page. One component serves three routes — /legal, /terms and /privacy — all
// rendering the same document; /terms and /privacy just land you on their half of it. The
// design mock is a single combined page (Part I: EULA, Part II: Security & Privacy Policy),
// and Part I §08 cross-references Part II inline, so splitting it into two pages would break
// that link and duplicate the masthead/TOC on both.
//
// Styling lives in styles/Legal.css (scoped under .legal-page) rather than MUI sx, matching
// About.jsx, Pricing.jsx and QuotePage.jsx — the mock is a large custom design whose reset
// fights MUI's generated styles.
//
// All copy is data below rather than inline markup, so a wording change is an edit to one
// array — same convention as TRACKS in Pricing.jsx and TOOLS in About.jsx. The table of
// contents renders itself from those arrays, so there's no second list to keep in step.
//
// No <footer> here: App.js renders the shared <HomeFooter /> on these routes.
//
// NOTE: this document is unreviewed by counsel. The bracketed [Date] / [State] / age
// placeholders and the dashed "Open item" boxes are deliberately visible — see PLACEHOLDERS
// near the bottom of this comment block for the full list of what still needs filling in:
//   - effective date, minimum age, governing-law state
//   - legal@ / marketing@ mailbox addresses (flagged inline)
//   - the data-architecture diagram, the arbitration-clause decision, the COPPA/CPRA review
//   - revisionari.com/cookiepolicy, which does not exist yet

import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import '../styles/Legal.css';

// Smooth in-page jump. preventDefault keeps react-router out of it, while the href is left in
// place so middle-click and keyboard focus still behave. Module-level so the copy arrays below
// can use it too. Same approach as About.jsx's scrollToId.
const jumpTo = (id) => (e) => {
  e.preventDefault();
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

// --- inline helpers used inside the copy ------------------------------------------------

// Bracketed value still to be confirmed before launch.
const Tag = ({ children }) => <span className="placeholder-tag">{children}</span>;

// Mailbox plus the "not yet confirmed" flag from the mock. Drop the flag (and this comment)
// once the addresses are live.
const Email = ({ addr }) => (
  <>
    <a href={`mailto:${addr}`} className="contact-email">{addr}</a>
    <span className="contact-flag">&larr; confirm before launch</span>
  </>
);

// Cross-reference to another section of this same document.
const Jump = ({ to, children }) => (
  <a href={`#${to}`} className="inline-link" onClick={jumpTo(to)}>{children}</a>
);

// --- PART I: End User License Agreement -------------------------------------------------

// Unnumbered preamble, rendered above section 01.
const EULA_INTRO = (
  <>
    This End User License Agreement (&ldquo;Agreement&rdquo;) is a legal agreement between you
    (&ldquo;User,&rdquo; &ldquo;you&rdquo;) and ReVisionari LLC. (&ldquo;Company,&rdquo;
    &ldquo;we,&rdquo; &ldquo;us&rdquo;) governing your use of the ReVisionari application and
    website (collectively, the &ldquo;Service&rdquo;). By creating an account or otherwise using
    the Service, you agree to be bound by this Agreement.
  </>
);

// `short` is the table-of-contents label; `title` is the heading on the section itself. They
// differ where the full heading is too long to sit in a two-column TOC.
const EULA_SECTIONS = [
  {
    id: 'eula-1',
    num: '01',
    short: 'Eligibility',
    title: 'Eligibility',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            You must be at least <Tag>[13 / 16 / 18 &middot; confirm with counsel]</Tag> years old
            to create an account. If you are under the age of majority in your jurisdiction, you
            may only use the Service with the involvement of a parent or legal guardian, to the
            extent required by applicable law.
          </>
        ),
      },
    ],
  },
  {
    id: 'eula-2',
    num: '02',
    short: 'License grant',
    title: 'License grant',
    blocks: [
      {
        t: 'p',
        c: 'Subject to your compliance with this Agreement, Company grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Service for its intended purpose, based on your account role (Author, Reviewer, or Reader).',
      },
    ],
  },
  {
    id: 'eula-3',
    num: '03',
    short: 'User roles & tiers',
    title: 'User roles and subscription tiers',
    blocks: [
      {
        t: 'p',
        c: "The Service offers distinct modes of engagement: Author, Reviewer, and Reader, each with tiered access as described on the Company's pricing page. Subscription fees, discounts, and print-plus-shipment costs for physical books are governed by the pricing terms in effect at the time of your purchase, and physical book purchases are non-refundable and non-returnable unless the item is defective.",
      },
    ],
  },
  {
    id: 'eula-4',
    num: '04',
    short: 'User content',
    title: 'User content',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            You retain ownership of any manuscript, commentary, or other content you upload to the
            Service (&ldquo;User Content&rdquo;), subject to any separate Author Publishing
            Agreement or Reviewer Engagement Agreement you have entered into with Company. By
            uploading User Content, you grant Company a license to host, display, and process that
            content as necessary to operate the Service, including the &ldquo;Idea
            Branching&rdquo; and community feedback features, based on the visibility settings you
            select.
          </>
        ),
      },
    ],
  },
  {
    id: 'eula-5',
    num: '05',
    short: 'Community feedback',
    title: 'Community feedback and idea branching',
    blocks: [
      {
        t: 'p',
        c: "If you open a section of your Work for community feedback, you understand and agree that vetted Reviewers or Readers, depending on your settings, will be able to view that content and submit comments through the Service's Dialogue Interface. You may adjust visibility settings for each section of your Work at any time, subject to feedback already submitted.",
      },
    ],
  },
  {
    id: 'eula-6',
    num: '06',
    short: 'Prohibited conduct',
    title: 'Prohibited conduct',
    blocks: [
      { t: 'p', c: 'You agree not to:' },
      {
        t: 'ul',
        items: [
          "Upload content that infringes another party's intellectual property rights",
          'Harass, defame, or threaten another user',
          "Attempt to reproduce, redistribute, or circumvent access restrictions on another user's manuscript content",
          'Use the Service to distribute malware or engage in unauthorized data collection',
          'Misrepresent your identity or authorization to act on behalf of another party',
        ],
      },
    ],
  },
  {
    id: 'eula-7',
    num: '07',
    short: 'Intellectual property',
    title: 'Intellectual property',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            The Service, including its software, design, &ldquo;Idea Branching&rdquo;
            functionality, and all associated trademarks, is the property of Company and its
            licensors. This Agreement does not grant you any right to use Company&rsquo;s
            trademarks, branding, or proprietary technology outside of your ordinary use of the
            Service.
          </>
        ),
      },
    ],
  },
  {
    id: 'eula-8',
    num: '08',
    short: 'Privacy',
    title: 'Privacy',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            Your use of the Service is also governed by Company&rsquo;s Security and Privacy
            Policy, which describes how Company collects, uses, and protects your personal data.
            See <Jump to="priv-objective">Part II</Jump> below.
          </>
        ),
      },
    ],
  },
  {
    id: 'eula-9',
    num: '09',
    short: 'Disclaimer of warranties',
    title: 'Disclaimer of warranties',
    blocks: [
      {
        t: 'caps',
        c: 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE," WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT, TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW.',
      },
    ],
  },
  {
    id: 'eula-10',
    num: '10',
    short: 'Limitation of liability',
    title: 'Limitation of liability',
    blocks: [
      {
        t: 'caps',
        c: "TO THE MAXIMUM EXTENT PERMITTED BY LAW, COMPANY'S TOTAL LIABILITY ARISING OUT OF OR RELATING TO THIS AGREEMENT WILL NOT EXCEED THE AMOUNT YOU PAID TO COMPANY IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM. COMPANY IS NOT LIABLE FOR INDIRECT, INCIDENTAL, OR CONSEQUENTIAL DAMAGES.",
      },
    ],
  },
  {
    id: 'eula-11',
    num: '11',
    short: 'Termination',
    title: 'Termination',
    blocks: [
      {
        t: 'p',
        c: 'Company may suspend or terminate your access to the Service if you violate this Agreement. You may terminate your account at any time; any ongoing royalty, contract, or subscription obligations survive termination as described in the applicable agreement.',
      },
    ],
  },
  {
    id: 'eula-12',
    num: '12',
    short: 'Governing law',
    title: 'Governing law and dispute resolution',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            This Agreement is governed by the laws of the State of <Tag>[State]</Tag>.
          </>
        ),
      },
      {
        t: 'note',
        label: 'Open item',
        c: "Confirm with counsel whether an arbitration clause and class-action waiver should be added, and ensure compliance with applicable consumer protection law in the user's jurisdiction.",
      },
    ],
  },
  {
    id: 'eula-13',
    num: '13',
    short: 'Changes to this agreement',
    title: 'Changes to this agreement',
    blocks: [
      {
        t: 'p',
        c: 'Company may update this Agreement from time to time. Material changes will be communicated to users through the Service or by email prior to taking effect. Continued use of the Service after changes take effect constitutes acceptance of the updated Agreement.',
      },
    ],
  },
  {
    id: 'eula-14',
    num: '14',
    short: 'Contact',
    title: 'Contact',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            Questions about this Agreement may be directed to Company at{' '}
            <Email addr="legal@revisionari.com" />.
          </>
        ),
      },
    ],
  },
];

// --- PART II: Security & Privacy Policy -------------------------------------------------

const PRIVACY_SECTIONS = [
  {
    id: 'priv-objective',
    num: '01',
    short: 'Objective',
    title: 'Objective',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            ReVisionari is committed to maintaining the integrity and security of confidential
            information, non-public information, and private information belonging to ReVisionari,
            its clients, and its partners. It is the policy of ReVisionari to prevent such
            information from being disclosed through the implementation of specific information
            security procedures, as described in this Security and Privacy Policy
            (&ldquo;Policy&rdquo;). The Policy sets forth ReVisionari&rsquo;s requirements with
            respect to the collection, storage, use, transmission, and disposal of information in
            electronic, voice, or other tangible written forms.
          </>
        ),
      },
      {
        t: 'p',
        c: (
          <>
            This Policy covers all personal data of the Company&rsquo;s employees, consultants,
            external vendors, clients, end customers of Company clients, and other natural persons,
            as well as all confidential information of the Company and any third parties who have
            provided confidential information to the Company (collectively, &ldquo;Confidential
            Information&rdquo;). This Policy applies to all employees, consultants, subcontractors,
            and agents (&ldquo;Company Personnel&rdquo;), as well as other third parties who access
            information in any ReVisionari-interfacing systems and websites.
          </>
        ),
      },
    ],
  },
  {
    id: 'priv-architecture',
    num: '02',
    short: 'Data architecture',
    title: 'Data architecture',
    blocks: [
      {
        t: 'note',
        label: 'Open item',
        c: "A diagram of ReVisionari's data architecture, reflecting our privacy- and security-by-design approach, will be included here.",
      },
    ],
  },
  {
    id: 'priv-design',
    num: '03',
    short: 'Information security design',
    title: 'Information security design',
    blocks: [
      {
        t: 'p',
        c: 'ReVisionari values the privacy of all individuals whose information is accessible to the Company and Company Personnel. The Company adheres to the following privacy principles:',
      },
      {
        t: 'ul',
        items: [
          'To collect only information that is necessary and relevant',
          'To maintain information in a secure manner',
          'To use reasonable practices and technology consistent with industry standards to safeguard the security and privacy of data',
          'To limit access to only those persons who have a legitimate business need to access the information',
          'To copy and disseminate information only as necessary to conduct Company business, perform services in accordance with client agreements, or comply with applicable laws and regulations',
          'To disclose information to Company Personnel and other third parties only after receiving reasonable written assurances regarding confidentiality',
        ],
      },
    ],
  },
  {
    id: 'priv-safeguards',
    num: '04',
    short: 'Safeguards',
    title: 'Safeguards',
    blocks: [
      {
        t: 'p',
        c: 'To protect Confidential Information, ReVisionari implements physical, technical, and administrative safeguards.',
      },
      { t: 'h4', c: 'Physical security' },
      {
        t: 'p',
        c: (
          <>
            <strong>Facility access and control.</strong> ReVisionari maintains a policy defining
            secure areas, including server rooms, network management centers, backup facilities,
            and communication rooms.
          </>
        ),
      },
      {
        t: 'p',
        c: (
          <>
            <strong>Security for non-electronic information.</strong> Company Personnel follow
            ReVisionari policies to protect Confidential Information in non-electronic form.
            Measures for highly sensitive information (including PII) include storage in locked
            file cabinets or other clearly delineated secure storage, located in offices kept
            secure both during and after business hours.
          </>
        ),
      },
      {
        t: 'p',
        c: (
          <>
            <strong>Removal and disposal of data.</strong> Materials containing Confidential
            Information are destroyed by shredding (if hard copy) or, if stored electronically,
            disposed of in a secure manner.
          </>
        ),
      },
      { t: 'h4', c: 'Technical safeguards' },
      {
        t: 'p',
        c: (
          <>
            <strong>Access control.</strong> ReVisionari implements technical policies and
            procedures that allow only authorized persons to access Confidential Information.
            Company Personnel responsible for designing, implementing, or managing Systems are
            required to comply with all ReVisionari policies for the protection of electronically
            stored information, including password protection, authorization protocols, encryption,
            and common-sense procedures to minimize the risk of theft, unauthorized access, change,
            or interruption.
          </>
        ),
      },
      { t: 'h4', c: 'Administrative safeguards' },
      {
        t: 'p',
        c: (
          <>
            <strong>Audit controls and monitoring.</strong> ReVisionari&rsquo;s internal audit,
            compliance, and information security functions evaluate compliance with this Policy and
            related procedures.
          </>
        ),
      },
      {
        t: 'p',
        c: (
          <>
            <strong>Security management process.</strong> ReVisionari identifies and analyzes
            potential risks to electronic Confidential Information and implements security measures
            that reduce risks and vulnerabilities to a reasonable and appropriate level, including
            network monitoring, intrusion detection, and periodic risk assessments conducted
            internally or by independent third parties.
          </>
        ),
      },
      {
        t: 'p',
        c: (
          <>
            <strong>Information access management.</strong> Access to Confidential Information is
            authorized only when appropriate to the user&rsquo;s or recipient&rsquo;s role.
            Role-based access limits exposure of particular items of Confidential Information to
            Company Personnel with a legitimate business need consistent with their job function.
          </>
        ),
      },
      {
        t: 'p',
        c: (
          <>
            <strong>Information security and privacy incident management.</strong> ReVisionari
            maintains an incident management process requiring security incidents to be effectively
            reported, remedied, investigated, and monitored, so corrective and preventive actions
            are taken to control and remediate incidents in a timely manner.
          </>
        ),
      },
      {
        t: 'note',
        label: 'Supplemental guidelines',
        c: 'Any supplemental guidelines or procedures referenced in this Policy may be obtained by contacting the Information Security Group. This Policy remains in force unless superseded by a later version; ReVisionari reserves the right to supplement, change, or discontinue any portion of this Policy at its sole discretion.',
      },
    ],
  },
  {
    id: 'priv-collection',
    num: '05',
    short: 'Information collection & use',
    title: 'Information collection and use',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            ReVisionari LLC. (&ldquo;us,&rdquo; &ldquo;we,&rdquo; or &ldquo;our&rdquo;) operates
            the{' '}
            <a
              href="https://www.revisionari.com"
              className="inline-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              revisionari.com
            </a>{' '}
            website and app (the &ldquo;Service&rdquo;) and respects your privacy. This Policy
            describes how ReVisionari uses personally identifiable information collected on the
            Service. By using the Service or providing personal information through one of our
            products, you agree to the collection and use of information as described here. Other
            than as expressly described in this Policy, we will not share, sell, or rent personally
            identifiable information with anyone outside ReVisionari, without your prior permission
            or unless ordered by a court of law.
          </>
        ),
      },
      { t: 'h4', c: 'Personal data' },
      {
        t: 'p',
        c: 'Personal Data is information about a living individual who can be identified from that data, alone or in combination with other information in our possession or likely to come into our possession. You may be asked to provide personally identifiable information on our website forms, including but not limited to:',
      },
      {
        t: 'ul',
        items: ['Email address', 'Name', 'Country, state, and city', 'Address', 'Phone number'],
      },
      {
        t: 'p',
        c: 'ReVisionari may use your Personal Data to contact you with emails, newsletters, marketing, and other information that may be of interest to you. You may opt out of any or all of these communications using the unsubscribe link or instructions provided in any email we send.',
      },
      { t: 'h4', c: 'Usage data' },
      {
        t: 'p',
        c: "Usage Data is collected automatically, either generated by use of revisionari.com or from your interaction with marketing communications (for example, opening or clicking on an email). This may include your device's IP address, browser type, the pages you visit, the time and date of your visit, time spent on those pages, unique device identifiers, and other diagnostic data.",
      },
      { t: 'h4', c: 'Tracking & cookies data' },
      {
        t: 'p',
        c: 'Cookies are small pieces of data stored on your device, which may include an anonymous unique identifier. We use cookies and similar technologies (including tags, beacons, and scripts) to track activity on revisionari.com and improve our Services. You may instruct your browser to refuse cookies, though some portions of the Service may not function properly without them. Examples of cookies we use:',
      },
      {
        t: 'ul',
        items: [
          'Session cookies',
          'Preference cookies, used to remember your settings',
          'Security cookies, used for security purposes',
        ],
      },
      {
        t: 'p',
        // Not a link — the cookie policy page does not exist yet. Turn this into an <a> once
        // /cookiepolicy ships.
        c: (
          <>
            Our full cookie policy is available at <Tag>revisionari.com/cookiepolicy</Tag>.
          </>
        ),
      },
    ],
  },
  {
    id: 'priv-use',
    num: '06',
    short: 'Use of data',
    title: 'Use of data',
    blocks: [
      { t: 'p', c: 'ReVisionari uses collected data to:' },
      {
        t: 'ul',
        items: [
          'Provide and maintain revisionari.com',
          'Notify you about changes to our Service',
          'Allow you to participate in interactive features when you choose to do so',
          'Improve our website',
          'Monitor usage of the website',
          'Detect and prevent technical problems',
          "Provide news, special offers, and information similar to what you've shown interest in, purchased, or inquired about, unless you've opted out",
        ],
      },
    ],
  },
  {
    id: 'priv-controller',
    num: '07',
    short: 'Data controller',
    title: 'Data controller',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            A Data Controller determines the purposes for which, and the manner in which, personal
            information is processed. ReVisionari is the Data Controller of your Personal Data as
            specified in this Policy. You may email us regarding your data at{' '}
            <Email addr="marketing@revisionari.com" />.
          </>
        ),
      },
    ],
  },
  {
    id: 'priv-gdpr',
    num: '08',
    short: 'GDPR',
    title: 'Processing personal data under GDPR',
    blocks: [
      {
        t: 'p',
        c: "If you are located in the European Economic Area (EEA), ReVisionari's legal basis for collecting and using your personal information depends on the data collected and the specific context. ReVisionari may process your Personal Data because:",
      },
      {
        t: 'ul',
        items: [
          'We need to perform a contract with you',
          'You have given us permission to do so',
          'The processing is in our legitimate interests and is not overridden by your rights',
          'We must comply with the law',
        ],
      },
    ],
  },
  {
    id: 'priv-retention',
    num: '09',
    short: 'Retention of data',
    title: 'Retention of data',
    blocks: [
      {
        t: 'p',
        c: 'ReVisionari retains your Personal Data only as long as necessary for the purposes set out in this Policy, including to comply with legal obligations, resolve disputes, and enforce our agreements and policies. Usage Data is generally retained for a shorter period, except where it is used to improve security or we are legally obligated to retain it longer.',
      },
    ],
  },
  {
    id: 'priv-transfer',
    num: '10',
    short: 'Transfer of data',
    title: 'Transfer of data',
    blocks: [
      {
        t: 'p',
        c: 'Your information, including Personal Data, may be transferred to and maintained on computers located outside your state, country, or other governmental jurisdiction, where data protection laws may differ. If you are located outside the United States and choose to provide information to us, note that we transfer that data to the United States and process it there; your consent to this Policy, followed by submission of such information, represents your agreement to that transfer. ReVisionari makes reasonable efforts to ensure your data is held securely and will not transfer it to an organization or country without adequate security controls in place.',
      },
    ],
  },
  {
    id: 'priv-disclosure',
    num: '11',
    short: 'Disclosure of data',
    title: 'Disclosure of data',
    blocks: [
      {
        t: 'p',
        c: 'ReVisionari may disclose your Personal Data in the good-faith belief that doing so is necessary to:',
      },
      {
        t: 'ul',
        items: [
          'Comply with a legal obligation',
          'Defend the rights or property of ReVisionari',
          'Prevent or investigate possible wrongdoing in connection with the Service',
          'Protect the personal safety of users of the Service or the public',
        ],
      },
    ],
  },
  {
    id: 'priv-security',
    num: '12',
    short: 'Security of data',
    title: 'Security of data',
    blocks: [
      {
        t: 'p',
        c: 'The security of your data matters to us, but no method of transmission over the internet or method of electronic storage is 100% secure. We use commercially acceptable means to protect your Personal Data, but we cannot guarantee its absolute security.',
      },
    ],
  },
  {
    id: 'priv-rights',
    num: '13',
    short: 'Your rights',
    title: 'Your rights',
    blocks: [
      {
        t: 'p',
        c: (
          <>
            You may have rights relating to your personal data under local data protection laws. If
            you are a resident of the European Economic Area (EEA), you have certain data
            protection rights under the GDPR, and ReVisionari aims to take reasonable steps to let
            you correct, amend, delete, or limit the use of your Personal Data. To find out what
            Personal Data we hold about you, or to request its removal, contact us at{' '}
            <Email addr="marketing@revisionari.com" />.
          </>
        ),
      },
      { t: 'p', c: 'In certain circumstances, you have the following rights:' },
      {
        t: 'ul',
        items: [
          'Access to the information we hold about you, which you can access, update, or request to delete directly within your account or forms',
          'Data portability, in the form of a copy of your information in a commonly used format',
          'Rectification, if your information is inaccurate or incomplete',
          'The right to object to our processing of your Personal Data',
          'Restriction, letting you request that we limit how we process your personal information',
          'The right to withdraw consent at any time where ReVisionari relied on your consent to process your data',
        ],
      },
      {
        t: 'p',
        c: 'We may ask you to verify your identity before responding to these requests. You also have the right to lodge a complaint with a Data Protection Authority regarding our collection and use of your Personal Data; contact your local data protection authority in the EEA for more information.',
      },
    ],
  },
  {
    id: 'priv-children',
    num: '14',
    short: "Children's privacy",
    title: "Children's privacy",
    blocks: [
      {
        t: 'p',
        c: (
          <>
            ReVisionari is committed to complying with the Children&rsquo;s Online Privacy
            Protection Act (COPPA) and applicable state-level privacy regulations, including the
            CPRA where relevant.
          </>
        ),
      },
      {
        t: 'note',
        label: 'Open item',
        c: 'Finalize the applicable minimum age, parental-consent flow, and state-by-state regulatory review (including CPRA) with counsel before launch.',
      },
    ],
  },
];

// Which half of the document each alias route opens on. /legal itself opens at the top.
const ROUTE_TARGETS = { '/terms': 'part-eula', '/privacy': 'part-privacy' };

// --- rendering --------------------------------------------------------------------------

const Block = ({ block }) => {
  switch (block.t) {
    case 'ul':
      return (
        <ul>
          {block.items.map((item) => <li key={item}>{item}</li>)}
        </ul>
      );
    case 'h4':
      return <h4>{block.c}</h4>;
    case 'caps':
      return <div className="caps-block">{block.c}</div>;
    case 'note':
      return (
        <div className="note-box">
          <span className="note-label">{block.label}</span>
          {block.c}
        </div>
      );
    default:
      return <p>{block.c}</p>;
  }
};

const Section = ({ section }) => (
  <div className="legal-section" id={section.id}>
    <h3>
      <span className="sec-num">{section.num}</span> {section.title}
    </h3>
    {section.blocks.map((block, i) => <Block key={i} block={block} />)}
  </div>
);

export default function Legal() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { startCognito } = useAuth();
  const rootRef = useRef(null);
  const [navOpen, setNavOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);

  // Scroll-reveal for .reveal blocks (the mock's inline IntersectionObserver, made React-safe).
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const els = root.querySelectorAll('.reveal');

    if (!('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('in'));
      return;
    }

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.08 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  // Scroll-spy: highlights the reader's current section in the table of contents. The
  // rootMargin narrows the "active" band to roughly the top sixth of the viewport, so the
  // highlight tracks what you're reading rather than whatever happens to be on screen.
  // Held in state rather than by toggling classes directly, so React stays the only thing
  // writing to the TOC's DOM.
  useEffect(() => {
    const root = rootRef.current;
    if (!root || !('IntersectionObserver' in window)) return;
    const sections = root.querySelectorAll('.legal-section[id]');

    const spy = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActiveId(entry.target.id);
        });
      },
      { rootMargin: '-15% 0px -70% 0px' }
    );
    sections.forEach((s) => spy.observe(s));
    return () => spy.disconnect();
  }, []);

  // /terms and /privacy open on their half of the document. ScrollToTop in App.js resets the
  // scroll position on every navigation, and its effect runs before this one (it sits earlier
  // in the tree), so this lands after it. The rAF waits for layout — measuring the offset of a
  // section this long before paint gives the wrong position.
  useEffect(() => {
    const target = ROUTE_TARGETS[pathname];
    if (!target) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(target)?.scrollIntoView({ block: 'start' });
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname]);

  // Service and Product have no routes yet — inert placeholders, matching /quote's treatment.
  const navItems = [
    { label: 'Service' },
    { label: 'Product' },
    { label: 'About', onClick: () => navigate('/about') },
    { label: 'Pricing', onClick: () => navigate('/pricing') },
  ];

  const tocGroups = [
    { title: 'I. End User License Agreement', sections: EULA_SECTIONS },
    { title: 'II. Security & Privacy Policy', sections: PRIVACY_SECTIONS },
  ];

  return (
    <div className="legal-page" ref={rootRef}>
      <header className="masthead">
        <div className="glow g1" />
        <div className="glow g2" />

        <div className="wrap">
          <nav>
            {/* Plain img rather than MUI — this page's stylesheet has a universal reset that
                fights MUI's generated styles. Sized in Legal.css. */}
            <Link to="/" className="site-logo" aria-label="ReVisionari home">
              <img src="/images/revisionari-logo.png" alt="ReVisionari" />
            </Link>

            <div className={`nav-links${navOpen ? ' open' : ''}`} id="legal-nav-links">
              {navItems.map((item) => (
                <a
                  key={item.label}
                  href={item.onClick ? '#top' : undefined}
                  onClick={item.onClick && ((e) => { e.preventDefault(); setNavOpen(false); item.onClick(); })}
                >
                  {item.label}
                </a>
              ))}
              <button type="button" onClick={() => { setNavOpen(false); startCognito('login'); }}>
                Login
              </button>
            </div>

            <button type="button" className="nav-cta" onClick={() => startCognito('signup')}>
              Get started
            </button>
            <button
              type="button"
              className={`nav-toggle${navOpen ? ' open' : ''}`}
              aria-expanded={navOpen}
              aria-controls="legal-nav-links"
              aria-label={navOpen ? 'Close menu' : 'Open menu'}
              onClick={() => setNavOpen((open) => !open)}
            >
              <span />
              <span />
              <span />
            </button>
          </nav>

          <div className="hero">
            <span className="eyebrow">Legal</span>
            <h1>Terms, Privacy &amp; Licensing</h1>
            <p>
              Our End User License Agreement and our Security &amp; Privacy Policy, in one place.
              Together they govern your use of the ReVisionari app and website.
            </p>
            <div className="doc-meta-row">
              <span className="doc-meta">Version 1.0</span>
              <span className="doc-meta">
                Effective <span className="placeholder-tag">[Date]</span>
              </span>
            </div>
          </div>
        </div>
      </header>

      <section className="legal-shell">
        <div className="wrap">

          {/* ============ TABLE OF CONTENTS ============ */}
          <nav className="toc reveal" aria-label="On this page">
            <span className="toc-label">On this page</span>

            <div className="toc-groups">
              {tocGroups.map((group) => (
                <div className="toc-group" key={group.title}>
                  <div className="toc-group-title">{group.title}</div>
                  <div className="toc-links">
                    {group.sections.map((s) => (
                      <a
                        key={s.id}
                        href={`#${s.id}`}
                        className={activeId === s.id ? 'current' : undefined}
                        aria-current={activeId === s.id ? 'true' : undefined}
                        onClick={jumpTo(s.id)}
                      >
                        {s.num}. {s.short}
                      </a>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="toc-tools-back">
              <a href="/about" onClick={(e) => { e.preventDefault(); navigate('/about'); }}>
                &larr; Back to About
              </a>
            </div>
          </nav>

          {/* ============ DOCUMENT BODY ============ */}
          <article className="legal-doc">

            {/* ============ PART I: EULA ============ */}
            <div className="doc-part reveal" id="part-eula">
              <div className="doc-part-head">
                <span className="eyebrow">Part I</span>
                <h2>End User License Agreement</h2>
                <p className="doc-sub">Applies to all users of the ReVisionari App and Website.</p>
              </div>

              <div className="legal-section">
                <p>{EULA_INTRO}</p>
              </div>

              {EULA_SECTIONS.map((s) => <Section key={s.id} section={s} />)}
            </div>

            {/* ============ PART II: SECURITY & PRIVACY POLICY ============ */}
            <div className="doc-part reveal" id="part-privacy">
              <div className="doc-part-head">
                <span className="eyebrow">Part II</span>
                <h2>Security &amp; Privacy Policy</h2>
                <p className="doc-sub">
                  How ReVisionari collects, secures, and uses information across the Service.
                </p>
              </div>

              {PRIVACY_SECTIONS.map((s) => <Section key={s.id} section={s} />)}
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
